import html2pdf from 'html2pdf.js';
import { NoteResponse } from '../api/notes';
import { getReferralLetterLogo } from './referralLetterLogos';

const GS_COMPANY_NAME = 'GetScanned Ltd - Company registration no. 15634790';
const GS_COMPANY_ADDRESS = 'Jactin House, 24 Hood Street, Ancoats, Manchester M4 6WX';

export type ReferralLetterPdfView = {
  url: string;
  filename: string;
};

export const getReferralLetterPdfFilename = (note: NoteResponse): string => {
  const safeTitle = (note.title || 'note').replace(/[<>:"/\\|?*]/g, '').trim() || 'note';
  return `${safeTitle}_${note.id}.pdf`;
};

export const downloadReferralLetterPdf = async (
  note: NoteResponse,
  downloadForGs: boolean,
  isView: boolean
): Promise<ReferralLetterPdfView | void> => {
  const element = document.createElement('div');
  const { src: logoSrc, style: logoStyle, alt: logoAlt } = getReferralLetterLogo(downloadForGs);

  const parser = new DOMParser();
  const doc = parser.parseFromString(note.description, 'text/html');

  doc.querySelectorAll('.tagged-user').forEach((tag) => {
    const figure = tag.closest('figure');
    if (figure) {
      figure.remove();
    } else {
      tag.remove();
    }
  });

  const cleanedHtml = doc.body.innerHTML;

  const gsFooterHtml = downloadForGs
    ? `
      <div style="text-align:center; margin-top:40px; padding-top:16px; font-size:11px; color:#505050; line-height:1.5;">
        <div>${GS_COMPANY_NAME}</div>
        <div>${GS_COMPANY_ADDRESS}</div>
      </div>
    `
    : '';

  element.innerHTML = `
    <div style="padding:24px; font-family:Arial,sans-serif;">
      <div style="text-align:center; margin-bottom:20px;">
        <img
          src="${logoSrc}"
          alt="${logoAlt}"
          style="${logoStyle}"
        />
      </div>
      ${cleanedHtml}
      ${gsFooterHtml}
    </div>
  `;

  document.body.appendChild(element);

  const logoImg = element.querySelector('img');
  if (logoImg) {
    await new Promise<void>((resolve, reject) => {
      if (logoImg.complete) {
        resolve();
        return;
      }
      logoImg.onload = () => resolve();
      logoImg.onerror = () => reject(new Error('Failed to load logo for PDF'));
    });
  }

  const filename = getReferralLetterPdfFilename(note);

  const worker = html2pdf()
    .set({
      margin: 10,
      filename,
      image: {
        type: 'jpeg',
        quality: 1,
      },
      html2canvas: {
        scale: 2,
        useCORS: true,
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
      },
    })
    .from(element);

  if (isView) {
    const pdf = await worker.toPdf().get('pdf');
    pdf.setProperties({
      title: filename.replace(/\.pdf$/i, ''),
    });
    const blob = pdf.output('blob');
    const file = new File([blob], filename, { type: 'application/pdf' });
    document.body.removeChild(element);
    return { url: URL.createObjectURL(file), filename };
  }

  await worker.save();
  document.body.removeChild(element);
};
